import { Fingerprint, ShieldCheck } from 'lucide-react'
import { Muted } from '@/components/sv/typographies'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import { ResourceScopeSelector } from '@/routes/api-keys-v2/create-api-key-sheet/resource-scope-selector'
import type { UseFormReturn } from 'react-hook-form'

type ApiKeyRole = {
  id: string
  name: string
  description: string | null
}

type ApiKeyPermission = {
  id: string
  name: string
  description: string | null
  resource: string
  action: string
}

type ResourceItem = {
  id: string
  displayName: string
}

export type CompanyResources = Record<string, ResourceItem[]>

type Props = {
  form: UseFormReturn<any>
  roles: ApiKeyRole[]
  permissions: ApiKeyPermission[]
  companyResources?: CompanyResources
  idPrefix?: string
}

type Capability = { permission: string }

const CARD_CLASS = cn(
  'relative has-[[data-state=checked]]:border-ring has-[[data-state=checked]]:bg-input/20',
  'flex flex-col gap-2 items-center rounded-lg border px-3 py-5 cursor-pointer'
)

const RADIO_CLASS = 'absolute top-3 left-3 data-[state=checked]:border-primary'

const RESOURCE_TYPE_TO_QUERY_KEY: Record<string, string> = {
  app: 'app',
  database: 'database',
  static_site: 'static_site',
  object_storage: 'object_storage',
  load_balancer: 'load_balancer',
  webhook: 'webhook',
  api_key: 'api_key',
  registry_credential: 'registry_credential',
}

const RESOURCE_DISPLAY_NAMES: Record<string, string> = {
  api_key: 'API key',
  registry_credential: 'Registry credential',
}

const buildPermission = (perm: ApiKeyPermission) =>
  `${perm.resource.toUpperCase()}:${perm.action.toUpperCase()}`

const formatResourceName = (resource: string) => {
  const key = resource.toLowerCase()
  if (RESOURCE_DISPLAY_NAMES[key]) {
    return RESOURCE_DISPLAY_NAMES[key]
  }
  return key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

const formatResourceLabel = (resource: string) => {
  const name = formatResourceName(resource)
  return `${name} resources`
}

export const PermissionSelector = ({
  form,
  roles,
  permissions,
  companyResources = {},
  idPrefix = '',
}: Props) => {
  const permissionMode = form.watch('permissionMode')
  const capabilities: Capability[] = form.watch('capabilities') ?? []
  const resourceScoping: Record<
    string,
    { mode: 'all' | 'specific'; ids: string[] }
  > = form.watch('resourceScoping') ?? {}

  const groupedPermissions = permissions.reduce<
    Record<string, ApiKeyPermission[]>
  >((acc, permission) => {
    const group = permission.resource
    if (!acc[group]) {
      acc[group] = []
    }
    acc[group].push(permission)
    return acc
  }, {})

  const ACTION_ORDER = ['create', 'read', 'update', 'delete']
  const uniqueActions = new Set(permissions.map((p) => p.action.toLowerCase()))
  const allActions = ACTION_ORDER.filter((a) => uniqueActions.has(a))

  const activeResourceTypes = Object.keys(groupedPermissions).filter(
    (resource) => {
      const perms = groupedPermissions[resource]
      return perms.some((p) =>
        capabilities.some((c) => c.permission === buildPermission(p))
      )
    }
  )

  return (
    <div className="space-y-6">
      <FormField
        control={form.control}
        name="permissionMode"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Permissions</FormLabel>
            <FormControl>
              <RadioGroup
                className="grid grid-cols-2 gap-3"
                value={field.value}
                onValueChange={(value) => {
                  field.onChange(value)
                  form.setValue('idRole', '')
                  form.setValue('capabilities', [])
                  form.setValue('resourceScoping', {})
                }}
              >
                <Label className={CARD_CLASS}>
                  <RadioGroupItem
                    value="role"
                    id={`${idPrefix}mode-role`}
                    className={RADIO_CLASS}
                  />
                  <div className="flex flex-col items-center gap-2">
                    <ShieldCheck className="size-5 shrink-0 mb-2 fill-emerald-600 stroke-background" />
                    <div className="font-medium">Predefined role</div>
                    <Muted className="text-center">
                      Use a predefined set of permissions.
                    </Muted>
                  </div>
                </Label>
                <Label className={CARD_CLASS}>
                  <RadioGroupItem
                    value="custom"
                    id={`${idPrefix}mode-custom`}
                    className={RADIO_CLASS}
                  />
                  <div className="flex flex-col items-center gap-2">
                    <Fingerprint className="size-5 shrink-0 mb-2" />
                    <div className="font-medium">Custom capabilities</div>
                    <Muted className="text-center">
                      Pick individual permissions.
                    </Muted>
                  </div>
                </Label>
              </RadioGroup>
            </FormControl>
          </FormItem>
        )}
      />

      {permissionMode === 'role' ? (
        <FormField
          control={form.control}
          name="idRole"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Role</FormLabel>
              <FormControl>
                <RadioGroup
                  className="grid gap-3"
                  value={field.value}
                  onValueChange={field.onChange}
                >
                  {roles.map((role) => (
                    <Label
                      key={role.id}
                      className={cn(
                        'relative has-[[data-state=checked]]:border-ring has-[[data-state=checked]]:bg-input/20',
                        'flex items-start gap-3 rounded-lg border p-4 cursor-pointer'
                      )}
                    >
                      <RadioGroupItem
                        value={role.id}
                        id={`${idPrefix}role-${role.id}`}
                        className="mt-0.5 shrink-0 data-[state=checked]:border-primary"
                      />
                      <div>
                        <div className="text-sm font-medium">
                          {formatResourceName(role.name)}
                        </div>
                        {role.description ? (
                          <Muted className="text-xs font-normal">
                            {role.description}
                          </Muted>
                        ) : null}
                      </div>
                    </Label>
                  ))}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}

      {permissionMode === 'custom' ? (
        <FormField
          control={form.control}
          name="capabilities"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Capabilities</FormLabel>
              <FormControl>
                <div className="rounded-lg border divide-y">
                  <div className="flex items-center px-4 py-2 bg-muted/50 text-xs font-medium text-muted-foreground">
                    <div className="flex-1">Resource</div>
                    {allActions.map((action) => {
                      const permsForAction = permissions.filter(
                        (p) => p.action.toLowerCase() === action
                      )
                      const allChecked =
                        permsForAction.length > 0 &&
                        permsForAction.every((p) =>
                          field.value?.some(
                            (c: Capability) =>
                              c.permission === buildPermission(p)
                          )
                        )
                      return (
                        <div
                          key={action}
                          className="w-20 flex flex-col items-center gap-1"
                        >
                          <span className="capitalize">{action}</span>
                          <Switch
                            checked={allChecked}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                const existing: Capability[] = field.value || []
                                const existingPerms = new Set(
                                  existing.map((c) => c.permission)
                                )
                                const toAdd = permsForAction
                                  .filter(
                                    (p) =>
                                      !existingPerms.has(buildPermission(p))
                                  )
                                  .map((p) => ({
                                    permission: buildPermission(p),
                                  }))
                                field.onChange([...existing, ...toAdd])
                              } else {
                                const permsToRemove = new Set(
                                  permsForAction.map((p) => buildPermission(p))
                                )
                                field.onChange(
                                  (field.value || []).filter(
                                    (c: Capability) =>
                                      !permsToRemove.has(c.permission)
                                  )
                                )
                              }
                            }}
                          />
                        </div>
                      )
                    })}
                  </div>
                  {Object.entries(groupedPermissions).map(
                    ([resource, perms]) => (
                      <div
                        key={resource}
                        className="flex items-center px-4 py-3"
                      >
                        <div className="flex-1 text-sm font-medium">
                          {formatResourceName(resource)}
                        </div>
                        {allActions.map((action) => {
                          const perm = perms.find(
                            (p) => p.action.toLowerCase() === action
                          )
                          if (!perm) {
                            return (
                              <div
                                key={action}
                                className="w-20 flex justify-center"
                              >
                                <Switch disabled checked={false} />
                              </div>
                            )
                          }
                          const permString = buildPermission(perm)
                          const isChecked = field.value?.some(
                            (c: Capability) => c.permission === permString
                          )
                          return (
                            <div
                              key={action}
                              className="w-20 flex justify-center"
                            >
                              <Switch
                                id={`${idPrefix}perm-${permString}`}
                                checked={isChecked}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    field.onChange([
                                      ...(field.value || []),
                                      { permission: permString },
                                    ])
                                  } else {
                                    field.onChange(
                                      (field.value || []).filter(
                                        (c: Capability) =>
                                          c.permission !== permString
                                      )
                                    )
                                  }
                                }}
                              />
                            </div>
                          )
                        })}
                      </div>
                    )
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ) : null}

      {permissionMode === 'custom' && activeResourceTypes.length > 0 ? (
        <div className="space-y-3">
          <div>
            <FormLabel>Resource scope</FormLabel>
            <Muted className="mt-1">
              Choose which specific resources each permission applies to. By
              default, permissions apply to all resources of that type.
            </Muted>
          </div>
          {activeResourceTypes.map((resource) => {
            const resourceKey =
              RESOURCE_TYPE_TO_QUERY_KEY[resource.toLowerCase()] ??
              resource.toLowerCase()
            const items = companyResources[resourceKey] ?? []
            const scoping = resourceScoping[resource.toLowerCase()] ?? {
              mode: 'all' as const,
              ids: [],
            }

            return (
              <ResourceScopeSelector
                key={resource}
                label={formatResourceLabel(resource)}
                items={items}
                mode={scoping.mode}
                selectedIds={scoping.ids}
                idPrefix={`${idPrefix}${resource.toLowerCase()}-`}
                onModeChange={(mode) => {
                  form.setValue('resourceScoping', {
                    ...resourceScoping,
                    [resource.toLowerCase()]: {
                      mode,
                      ids: mode === 'all' ? [] : scoping.ids,
                    },
                  })
                }}
                onSelectedIdsChange={(ids) => {
                  form.setValue('resourceScoping', {
                    ...resourceScoping,
                    [resource.toLowerCase()]: {
                      mode: 'specific',
                      ids,
                    },
                  })
                }}
              />
            )
          })}
        </div>
      ) : null}
    </div>
  )
}
